/** Formatting helpers shared by every screen. */

import { format, formatDistanceToNowStrict, isValid, parseISO } from "date-fns";

export function asDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = typeof value === "string" ? parseISO(value) : value;
  return isValid(date) ? date : null;
}

export function formatDate(value: string | Date | null | undefined, fallback = ""): string {
  const date = asDate(value);
  return date ? format(date, "dd MMM yyyy") : fallback;
}

export function formatDateTime(value: string | Date | null | undefined, fallback = ""): string {
  const date = asDate(value);
  return date ? format(date, "dd MMM yyyy HH:mm") : fallback;
}

export function formatRelative(value: string | Date | null | undefined, fallback = ""): string {
  const date = asDate(value);
  if (!date) return fallback;
  const suffix = date.getTime() > Date.now() ? "from now" : "ago";
  return `${formatDistanceToNowStrict(date)} ${suffix}`;
}

/** File and database sizes: 1.4 MB, 812 KB, 3.2 GB. Binary units, one decimal above KB. */
export function formatBytes(bytes: number | null | undefined, fallback = ""): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return fallback;
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 100 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/** Turnaround and SLA figures read better as "2 d 4 h" than "52 hours". */
export function formatHours(hours: number | null | undefined): string {
  if (hours === null || hours === undefined) return "";
  if (hours < 1) return "< 1 h";
  if (hours < 48) return `${Math.round(hours)} h`;
  const days = Math.floor(hours / 24);
  const rest = Math.round(hours % 24);
  return rest ? `${days} d ${rest} h` : `${days} d`;
}

export function formatNumber(value: number | string | null | undefined, digits = 0): string {
  if (value === null || value === undefined || value === "") return "";
  const number = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(number)) return String(value);
  return number.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function formatMoney(
  value: number | string | null | undefined,
  currency = "ETB",
): string {
  if (value === null || value === undefined) return "";
  const number = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(number)) return "";
  return `${currency} ${number.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

/** Countdown or overdue label for a deadline. */
export function dueLabel(due: string | null | undefined): {
  text: string;
  tone: "ok" | "soon" | "late" | "none";
} {
  const date = asDate(due);
  if (!date) return { text: "", tone: "none" };
  const hours = (date.getTime() - Date.now()) / 3_600_000;
  if (hours < 0) return { text: `overdue by ${formatHours(Math.abs(hours))}`, tone: "late" };
  if (hours < 12) return { text: `due in ${formatHours(hours)}`, tone: "soon" };
  return { text: `due ${formatDate(date)}`, tone: "ok" };
}

export function initials(name: string | null | undefined): string {
  if (!name) return "?";
  return name
    .replace(/\b(dr|prof|ato|w\/ro|w\/t|mr|mrs|ms)\.?\s+/gi, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * Articles, coordinating conjunctions and short prepositions stay lowercase
 * inside a title ("Return for Correction", "Certificates of Analysis"); every
 * other word is capitalised.
 */
const MINOR_WORDS = new Set([
  "a", "an", "the",
  "and", "but", "or", "nor", "for", "so", "yet",
  "as", "at", "by", "in", "of", "off", "on", "per", "to", "up", "via", "vs", "vs.",
]);

function capitalise(word: string): string {
  const index = word.search(/\p{L}/u);
  if (index < 0) return word;
  return word.slice(0, index) + word.charAt(index).toUpperCase() + word.slice(index + 1);
}

/**
 * Title case for titles, buttons, tabs and field labels: "New test request"
 * becomes "New Test Request".
 *
 * Only ever raises a letter, never lowers one, so acronyms and codes survive as
 * written ("SOPs", "pH", "REQ-2026-0001"). A minor word is lowered only when it
 * is not first or last and was written in plain lower or sentence case. Parts of
 * a hyphenated or slashed word are treated as words of their own ("Hand-Over").
 *
 * Anything that is not a string (an element, a number, null) is returned as it
 * came, so a `ReactNode` title can be passed straight through.
 */
export function toTitleCase(text: string): string;
export function toTitleCase<T>(text: T): T;
export function toTitleCase(text: unknown): unknown {
  if (typeof text !== "string") return text;
  const words = text.split(/(\s+)/);
  let lastWord = words.length - 1;
  while (lastWord > 0 && words[lastWord]!.trim() === "") lastWord--;
  let startsClause = true;
  return words
    .map((part, index) => {
      if (part.trim() === "") return part;
      const edge = startsClause || index === lastWord;
      startsClause = /[:.!?]$/.test(part);
      return part
        .split(/([-/])/)
        .map((piece, pieceIndex) => {
          if (piece === "-" || piece === "/") return piece;
          // Mixed case or digits: an acronym, a code, a unit. Leave it alone.
          if (/\d/.test(piece) || /\p{Lu}/u.test(piece.slice(1))) return piece;
          const bare = piece.toLowerCase().replace(/^[^\p{L}]+|[^\p{L}.]+$/gu, "");
          if (!(edge && pieceIndex === 0) && MINOR_WORDS.has(bare)) return piece.toLowerCase();
          return capitalise(piece);
        })
        .join("");
    })
    .join("");
}

export function cn(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(" ");
}
